import { useCallback, useRef, useState } from 'react';
import { uploadToSignedUrl } from '../upload/signed-url-upload';
/**
 * Chunked upload to a pre-signed URL.
 *
 * On Azure this mirrors the upload approach used by Autodesk Construction Cloud
 * and Bentley iTwin for large AEC files: the file is split into 4–8MB blocks
 * uploaded in parallel via `stageBlock`, then committed via `commitBlockList`.
 * The Azure SDK does the chunking; this hook is a thin React wrapper with
 * progress, abort, and error state.
 *
 * On GCS the block options have no effect and the file uploads as one PUT — see
 * `uploadToSignedUrl`. Progress, abort and error behaviour are the same either
 * way, so this hook's contract does not change.
 *
 * NOT a replacement for `useFileUpload` — that hook creates a core-api File
 * row and writes a signed URL there. This one talks to the signed URL only.
 */
export const useChunkedBlobUpload = (options) => {
    const blockSize = options?.blockSize ?? 4 * 1024 * 1024;
    const maxConcurrency = options?.maxConcurrency ?? 4;
    const [loaded, setLoaded] = useState(0);
    const [total, setTotal] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const abortControllerRef = useRef(null);
    const upload = useCallback(async (uploadUrl, file) => {
        setError(null);
        setLoaded(0);
        setTotal(file.size);
        setIsUploading(true);
        const controller = new AbortController();
        abortControllerRef.current = controller;
        try {
            await uploadToSignedUrl(uploadUrl, file, {
                blockSize,
                concurrency: maxConcurrency,
                abortSignal: controller.signal,
                onProgress: (ev) => {
                    setLoaded(ev.loadedBytes);
                },
            });
            setLoaded(file.size);
        }
        catch (err) {
            const e = err instanceof Error ? err : new Error(String(err));
            setError(e);
            throw e;
        }
        finally {
            setIsUploading(false);
            abortControllerRef.current = null;
        }
    }, [blockSize, maxConcurrency]);
    const abort = useCallback(() => {
        abortControllerRef.current?.abort();
    }, []);
    const progress = total > 0 ? loaded / total : 0;
    return { upload, abort, loaded, total, progress, isUploading, error };
};
